import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, Section, ReqTable } from "./LegalLayout";

const PrivacyEn = () => (
  <LegalLayout
    title="Privacy Policy"
    updated="June 14, 2026"
    backLabel="← Back to Home"
    updatedPrefix="Updated"
  >
    <Section title="1. Data Controller">
      <p>
        The data controller is the sole trader KOKOV ALEKSANDR DMITRIEVICH,
        OGRNIP: 325784700333162, INN: 784302022304, address: Russia,
        Saint Petersburg, Komarovo, Tsvetochnaya 2A (hereinafter — "Controller",
        "we").
      </p>
      <p>
        This Policy is developed in accordance with Federal Law No. 152-FZ of
        27.07.2006 "On Personal Data" and Regulation (EU) 2016/679 (GDPR), and
        applies to the website <strong>swingmentor.pro</strong>.
      </p>
    </Section>

    <Section title="2. Data We Process">
      <ul>
        <li>
          <strong>Account data:</strong> first name, last name, email address,
          password hash, golf handicap.
        </li>
        <li>
          <strong>User content:</strong> original and processed golf swing
          videos, phase images, text recommendations, scores, and structured
          analysis results.
        </li>
        <li>
          <strong>Payment data:</strong> payment confirmation, amount, and
          payment identifier. Bank card details are processed exclusively by the
          payment provider and are not transmitted to us. For crypto payments
          via Trybit, we receive and store only the payment confirmation, amount,
          currency, order identifier, and payment status.
        </li>
        <li>
          <strong>Technical data:</strong> cookies required for authentication
          and service operation.
        </li>
      </ul>
      <p>
        Special categories of data (health, biometrics, etc.) are not
        intentionally processed by us.
      </p>
    </Section>

    <Section title="3. Purposes and Legal Bases">
      <ReqTable
        rows={[
          ["Account registration and service access", "Performance of contract (Art. 6(1)(b) GDPR; Art. 6(1)(5) 152-FZ)"],
          ["Golf swing video analysis and recommendations", "Performance of contract; consent of the data subject"],
          ["Payment processing, issuing receipts", "Performance of contract; applicable fiscal law"],
          ["User support via email", "Performance of contract"],
          ["Compliance with applicable law", "Legal obligation (Art. 6(1)(c) GDPR)"],
        ]}
      />
    </Section>

    <Section title="4. Data Location and Transfers">
      <p>
        The location of personal data storage is determined by the user's region
        set at registration:
      </p>
      <p>
        <strong>Users from the Russian Federation.</strong> Collection, recording,
        systematisation, accumulation, storage, updating, and retrieval of
        personal data of Russian citizens are carried out using databases located
        in the Russian Federation (Saint Petersburg), in accordance with Art. 18
        of Federal Law No. 152-FZ.
      </p>
      <p>
        <strong>Users from other countries (EU, Asia, and other regions).</strong>{" "}
        Account data, analysis history, and media files of such users are stored
        on a server located in the Federal Republic of Germany (EU). Processing
        is carried out in accordance with Regulation (EU) 2016/679 (GDPR). Data
        of EU users is not transferred or copied to servers outside the EU,
        except as provided in this Policy.
      </p>
      <p>
        <strong>Video analysis.</strong> To perform automated analysis, a
        temporary copy of the video along with a technical job identifier may be
        transmitted to an AI infrastructure provider. Name, email address,
        payment details, and other account data are not shared with that node.
        The temporary copy is deleted after processing is complete.
      </p>
      <p>Third parties:</p>
      <ul>
        <li>
          <strong>YooKassa (YooMoney NKCO LLC)</strong> — payment processing for
          users from Russia, in accordance with PCI DSS.
        </li>
        <li>
          <strong>Trybit</strong> — processing of international crypto payments.
          Only technical payment details required to activate the plan are shared
          with the Controller.
        </li>
        <li>
          <strong>AI infrastructure providers</strong> — automated video
          processing without linkage to account data.
        </li>
      </ul>
      <p>
        Personal data is not sold or transferred to third parties for advertising
        or commercial purposes.
      </p>
    </Section>

    <Section title="5. Retention Periods">
      <ul>
        <li>
          Account data — for as long as the account exists or as long as
          retention is necessary to perform the contract and comply with legal
          obligations.
        </li>
        <li>
          Original video and temporary processing files — for the duration of the
          analysis and any provided retry attempts. Automatically deleted after
          successful completion or final failure of the job.
        </li>
        <li>
          Temporary copy of the video on an AI node — until the processing
          attempt is complete, then deleted.
        </li>
        <li>
          Processed video — while video saving is enabled and the account exists.
          When the setting is disabled, previously saved processed videos are
          deleted.
        </li>
        <li>
          Analysis text, scores, recommendations, phase images, and training
          history — while the account exists or until a confirmed deletion
          request is fulfilled.
        </li>
        <li>
          Payment transaction data — 5 years, in accordance with accounting
          requirements.
        </li>
      </ul>
      <p>
        Upon account deletion, profile details, analysis history, and associated
        media files are deleted. Upon a confirmed individual request, data is
        deleted within 30 days, except for data the Controller is required to
        retain by law.
      </p>
    </Section>

    <Section title="6. Your Rights">
      <p>You have the right to:</p>
      <ul>
        <li>obtain confirmation of processing and a copy of your data;</li>
        <li>request correction of inaccurate data;</li>
        <li>request erasure of your data ("right to be forgotten");</li>
        <li>
          withdraw previously given{" "}
          <Link to="/legal/consent-en">consent to processing</Link>;
        </li>
        <li>
          lodge a complaint with Roskomnadzor (for Russian users):{" "}
          <a href="https://rkn.gov.ru" target="_blank" rel="noopener noreferrer">
            rkn.gov.ru
          </a>.
        </li>
      </ul>
      <p>
        To exercise your rights, send a request to{" "}
        <a href="mailto:info@swingmentor.pro">info@swingmentor.pro</a> with the
        subject "Personal Data". We will respond within 10 business days.
      </p>
    </Section>

    <Section title="6a. Additional Rights for EU Users (GDPR)">
      <p>
        If you are located in the European Union, in addition to the rights
        listed in Section 6, you also have the right to:
      </p>
      <ul>
        <li>data portability (receive your data in a machine-readable format);</li>
        <li>restriction of processing;</li>
        <li>
          lodge a complaint with the data protection supervisory authority in
          your country.
        </li>
      </ul>
      <p>
        Legal bases for processing EU users' data: performance of contract
        (Art.&nbsp;6(1)(b) GDPR), consent of the data subject
        (Art.&nbsp;6(1)(a) GDPR), legitimate interests of the Controller
        (Art.&nbsp;6(1)(f) GDPR).
      </p>
    </Section>

    <Section title="7. Data Security">
      <p>
        The Controller implements necessary legal, organisational, and technical
        security measures: data transmission over HTTPS, storage of passwords as
        strong cryptographic hashes, access controls, and regular backups.
      </p>
    </Section>

    <Section title="8. Cookies">
      <p>
        The website uses technical cookies required for operation (authentication,
        language and currency preference storage). Marketing cookies and tracking
        pixels are not used.
      </p>
    </Section>

    <Section title="9. Contact">
      <p>
        For questions regarding personal data processing:{" "}
        <a href="mailto:info@swingmentor.pro">info@swingmentor.pro</a>, tel.
        +7 999 032-27-57.
      </p>
    </Section>
  </LegalLayout>
);

export default PrivacyEn;
